import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../hooks/api.js';
import { useAuthStore } from '../stores/authStore.js';
import { useEditorStore } from '../stores/editorStore.js';
import {
  collectVariableValues,
  initialVariableInputs,
  parseVariableDefinitions,
  validateVariableValues,
  variableInputErrors,
  variablePlaceholder,
  type Block,
  type EmailSender,
} from '@blogforge/shared';

interface Props {
  emailId: string;
  subject: string;
  blocks: Block[];
  /**
   * The email's stored `variableDefinition` (raw JSON from the email row).
   * When it holds at least one variable, the modal asks for a value per
   * variable and sends them back as `{ name: value }`.
   */
  variableDefinition?: unknown;
  onClose: () => void;
}

interface FormState {
  host: string;
  port: string;
  secure: boolean;
  user: string;
  pass: string;
  from: string;
  fromName: string;
  to: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'success'; messageId?: string; accepted: unknown[]; rejected: unknown[] }
  | { kind: 'error'; message: string };

const DEFAULTS: FormState = {
  host: '',
  port: '587',
  secure: false,
  user: '',
  pass: '',
  from: '',
  fromName: '',
  to: '',
};


export function SendTestEmailModal({
  emailId,
  subject,
  blocks,
  variableDefinition,
  onClose,
}: Props) {
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  // Stored SMTP senders the user may send through ("use"); credentials
  // never leave the server — only the sender id is sent.
  const { data: sendersData } = useQuery({
    queryKey: ['email-senders', activeWorkspaceId],
    queryFn: () => api.emailSenders.list(),
    enabled: Boolean(activeWorkspaceId),
    staleTime: 30_000,
  });
  const senders: EmailSender[] = (sendersData?.data ?? []).filter((s) => s.senderType === 'smtp');

  const [form, setForm] = useState<FormState>(DEFAULTS);
  // Sender id of the chosen stored sender; '' = none chosen yet, 'custom' = manual SMTP.
  const [senderId, setSenderId] = useState<string>('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  // ── Variables ────────────────────────────────────────────────
  // Only the definitions stored on the email matter; malformed entries are
  // skipped, an empty (or absent) record means there is nothing to ask for.
  const definitions = parseVariableDefinitions(variableDefinition);
  const variableNames = Object.keys(definitions).sort();
  const [variableInputs, setVariableInputs] = useState<Record<string, string>>(() =>
    initialVariableInputs(definitions),
  );
  const [variableErrors, setVariableErrors] = useState<Record<string, string>>({});

  const setVariable = (name: string, value: string) => {
    setVariableInputs((inputs) => ({ ...inputs, [name]: value }));
    // Editing an input clears whatever the last validation said about it.
    setVariableErrors((errors) => {
      if (errors[name] === undefined) return errors;
      const next = { ...errors };
      delete next[name];
      return next;
    });
  };

  // Default to the workspace's active sender when one is available —
  // but never override a manual "Custom SMTP" selection once made.
  useEffect(() => {
    if (senderId === 'custom') return;
    const active = senders.find((s) => s.isActive);
    if (active) setSenderId(active.id);
    else if (senders.length > 0) setSenderId(senders[0]!.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, senders.length]);

  const useManual = senderId === '' || senderId === 'custom';

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Variables are checked here and again on the test-send endpoint, so a
    // value that does not fit its definition never reaches the server.
    let variables: Record<string, unknown> | undefined;
    if (variableNames.length > 0) {
      const checked = validateVariableValues(
        definitions,
        collectVariableValues(definitions, variableInputs),
      );
      if (!checked.success) {
        setVariableErrors(variableInputErrors(checked.errors));
        setStatus({
          kind: 'error',
          message: 'Some variable values need fixing — see the fields above.',
        });
        return;
      }
      setVariableErrors({});
      variables = checked.data;
    }

    setStatus({ kind: 'sending' });
    try {
      const componentUsages = useEditorStore.getState().componentUsages;
      // Persist the current editor state first so the server builds the latest version
      await api.emails.update(emailId, { blocks, subject, componentUsages });

      const delivery =
        !useManual && senderId
          ? { senderId, to: form.to }
          : {
              smtp: {
                host: form.host,
                port: Number(form.port),
                secure: form.secure,
                user: form.user || undefined,
                pass: form.pass || undefined,
                from: form.from || undefined,
                fromName: form.fromName || undefined,
              },
              to: form.to,
            };

      const res = await api.emails.testSend(emailId, {
        ...delivery,
        ...(variables !== undefined ? { variables } : {}),
      });
      const info = res as { messageId?: string; accepted: unknown[]; rejected: unknown[] };
      setStatus({
        kind: 'success',
        ...(info.messageId ? { messageId: info.messageId } : {}),
        accepted: info.accepted ?? [],
        rejected: info.rejected ?? [],
      });
    } catch (err) {
      setStatus({
        kind: 'error',
        message: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const inputCls =
    'field-input' + (status.kind === 'error' ? ' border-danger/30 focus:border-danger/50' : '');

  return (
    <div
      className="modal-backdrop"
      onClick={() => {
        if (status.kind !== 'sending') onClose();
      }}
    >
      <div className="modal-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-ink text-[17px] font-bold tracking-tight">Send test email</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={status.kind === 'sending'}
            className="text-muted hover:bg-card-2 hover:text-ink-soft rounded p-1 disabled:opacity-40"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <p className="text-ink-soft mb-4 text-sm">
          Builds the current email and sends it to your inbox
          {senders.length > 0 ? ' through one of your saved senders' : ' over SMTP'}.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ── Sender source ────────────────────────────────── */}
          {senders.length > 0 && (
            <div>
              <label className="field-label">Send through</label>
              <select
                className="field-input w-full cursor-pointer"
                value={senderId}
                onChange={(e) => setSenderId(e.target.value)}
              >
                {senders.map((s) => {
                  const data = s.data as Record<string, unknown>;
                  const endpoint = (data['from'] as string) || (data['host'] as string) || '';
                  return (
                    <option key={s.id} value={s.id}>
                      {s.name}
                      {endpoint ? ` — ${endpoint}` : ''}
                    </option>
                  );
                })}
                <option value="custom">Custom SMTP (not saved)</option>
              </select>
            </div>
          )}

          {/* ── Destination ─────────────────────────────────── */}
          <div>
            <label className="field-label">Send to</label>
            <input
              type="email"
              required
              className={inputCls}
              value={form.to}
              onChange={(e) => set('to', e.target.value)}
              placeholder="you@example.com"
              autoFocus
            />
          </div>

          {/* ── Variables (only when the email defines any) ──── */}
          {variableNames.length > 0 && (
            <div className="border-line border-t pt-4">
              <p className="field-label">Variables</p>
              <p className="text-muted mb-3 text-xs">
                This email defines variables — set the values to build the test with.
                {variableNames.some((name) => definitions[name]?.required) && (
                  <span className="text-danger"> * required</span>
                )}
              </p>

              <div className="space-y-3">
                {variableNames.map((name) => {
                  const definition = definitions[name]!;
                  const error = variableErrors[name];
                  const fieldCls =
                    'field-input' +
                    (error !== undefined ? ' border-danger/30 focus:border-danger/50' : '');
                  const value = variableInputs[name] ?? '';

                  return (
                    <div key={name}>
                      <label className="field-label">
                        {name}
                        {definition.required && <span className="text-danger"> *</span>}
                      </label>

                      {definition.type === 'boolean' ? (
                        <select
                          className={`${fieldCls} w-full cursor-pointer`}
                          value={value}
                          onChange={(e) => setVariable(name, e.target.value)}
                        >
                          <option value="">
                            {definition.required ? 'Select a value…' : '— not set —'}
                          </option>
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : definition.type === 'enum' ? (
                        <select
                          className={`${fieldCls} w-full cursor-pointer`}
                          value={value}
                          onChange={(e) => setVariable(name, e.target.value)}
                        >
                          <option value="">
                            {definition.required ? 'Select a value…' : '— not set —'}
                          </option>
                          {definition.enum.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ) : definition.type === 'list' ? (
                        // A list's value is its items, so the field holds JSON —
                        // multi-line, because one item already is.
                        <textarea
                          rows={3}
                          className={`${fieldCls} font-mono`}
                          value={value}
                          onChange={(e) => setVariable(name, e.target.value)}
                          placeholder={variablePlaceholder(definition)}
                        />
                      ) : (
                        <input
                          className={fieldCls}
                          value={value}
                          onChange={(e) => setVariable(name, e.target.value)}
                          placeholder={variablePlaceholder(definition)}
                        />
                      )}

                      {definition.description && (
                        <p className="text-muted mt-1 text-xs">{definition.description}</p>
                      )}
                      {error !== undefined && <p className="text-danger mt-1 text-xs">{error}</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Manual SMTP (only when no saved sender chosen) ─ */}
          {useManual && (
            <>
              <div className="border-line border-t pt-4">
                <p className="field-label">SMTP server</p>
                <div className="grid grid-cols-[1fr_90px] gap-2">
                  <div>
                    <label className="field-label">Host</label>
                    <input
                      required
                      className={inputCls}
                      value={form.host}
                      onChange={(e) => set('host', e.target.value)}
                      placeholder="smtp.example.com"
                    />
                  </div>
                  <div>
                    <label className="field-label">Port</label>
                    <input
                      type="number"
                      min={1}
                      max={65535}
                      required
                      className={inputCls}
                      value={form.port}
                      onChange={(e) => set('port', e.target.value)}
                    />
                  </div>
                </div>

                <label className="mt-3 flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.secure}
                    onChange={(e) => set('secure', e.target.checked)}
                    className="border-line-strong rounded"
                  />
                  <span className="text-ink-soft text-xs">
                    Use SSL/TLS (implicit TLS, usually port 465)
                  </span>
                </label>
              </div>

              {/* ── Credentials ─────────────────────────────────── */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="field-label">Username</label>
                  <input
                    className={inputCls}
                    value={form.user}
                    onChange={(e) => set('user', e.target.value)}
                    placeholder="Optional"
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label className="field-label">Password</label>
                  <input
                    type="password"
                    className={inputCls}
                    value={form.pass}
                    onChange={(e) => set('pass', e.target.value)}
                    placeholder="Optional"
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <div>
                <label className="field-label">From address</label>
                <input
                  type="email"
                  className={inputCls}
                  value={form.from}
                  onChange={(e) => set('from', e.target.value)}
                  placeholder="sender@example.com (falls back to username)"
                />
              </div>

              <div>
                <label className="field-label">From name</label>
                <input
                  className={inputCls}
                  value={form.fromName}
                  onChange={(e) => set('fromName', e.target.value)}
                  placeholder="Optional, e.g. Eventor"
                />
                <p className="text-muted mt-1 text-xs">
                  Shown as “From name &lt;sender@example.com&gt;” in the recipient's inbox.
                </p>
              </div>
            </>
          )}

          {/* ── Status / errors ─────────────────────────────── */}
          {status.kind === 'success' && (
            <div className="bg-ok/10 text-ok rounded-md p-3 text-sm">
              <strong>Sent.</strong> Message ID: {status.messageId ?? 'n/a'}
              {status.accepted.length > 0 && (
                <div className="mt-1 text-xs">Accepted: {status.accepted.join(', ')}</div>
              )}
              {status.rejected.length > 0 && (
                <div className="text-danger mt-1 text-xs">
                  Rejected: {status.rejected.join(', ')}
                </div>
              )}
            </div>
          )}
          {status.kind === 'error' && (
            <div className="bg-danger/8 text-danger rounded-md p-3 text-sm">
              <strong>Send failed:</strong> {status.message}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={status.kind === 'sending'}
              className="btn-secondary rounded-lg px-4 py-2 text-sm disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={status.kind === 'sending'}
              className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {status.kind === 'sending' ? 'Building & sending…' : 'Send test email'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
